import cn from 'classnames';
import React, { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';

// TYPES
import { DOM_ID } from '@UILayer/UILayer.types';
import { IMenuPagesPopupProps } from './MenuPages.types';

// DATA
import { MenuPages } from './pages';

// STYLES
import styles from '@UILayer/UILayer.scss';

// REDUX
import {
    getCurrentGameState,
    getDeviceInfo,
    getGameHistoryState,
    getGlobalStyles,
    getInGameHistoryMode,
    getWCAMode,
    setGlobalStyles,
    setInGameHistoryMode,
    setPopupMessage
} from '@Managers/Redux/AppState/AppState';
import { useAppDispatch, useAppSelector } from '@Managers/Redux/Hooks';
import { Button } from '@UILayer/Inputs/Button';

// IMAGES
import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { EMessagesTypes } from '../PopupMessage';
import { HomeIcon, InGameHistoryIcon } from './pages';

const MenuPagesPopup: React.FC<IMenuPagesPopupProps> = ({ onClose, isPopupOpen }) => {
    const [state, setState] = useReducer((state: { homeButton: boolean; inGameHistoryButton: boolean }, newState: any) => ({ ...state, ...newState }), {
        homeButton: false,
        inGameHistoryButton: false
    });
    const handleOverlayClick = (e: React.MouseEvent<HTMLElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    const pages = MenuPages;
    const { t } = useTranslation();
    const [focusedIdx, setFocusedIdx] = useState<number>(0);
    const device = useAppSelector(getDeviceInfo);
    const globalStyles = useAppSelector(getGlobalStyles);
    const wcaMode = useAppSelector(getWCAMode);
    const inGameHistoryMode = useAppSelector(getInGameHistoryMode);
    const gameStoreGameHistoryState = useAppSelector(getGameHistoryState);
    const gameStoreCurrentGameState = useAppSelector(getCurrentGameState);
    const dispatch = useAppDispatch();

    useEffect(() => {
        setState({
            homeButton: ConfigLoader.getConfiguration(EConfigurationNames.UI_LAYER).buttons.home
        });
    }, []);

    useEffect(() => {
        if (ConfigLoader.getConfiguration(EConfigurationNames.UI_LAYER).inGameHistory) {
            let showButton = true;

            if (gameStoreGameHistoryState.spinsHistory.length === 0) showButton = false;
            if (gameStoreCurrentGameState != EGameStates.IDLE_STATE) showButton = false;

            setState({ inGameHistoryButton: showButton });
        }
    }, [gameStoreGameHistoryState, gameStoreCurrentGameState]);

    const containerCn = cn(
        styles.settings_overlay,
        globalStyles.orientation,
        globalStyles.handMode,
        globalStyles.device,
        globalStyles.wcaMode,
        globalStyles.wideDevice,
        globalStyles.screenSize,
        globalStyles.isTablet,
        {
            [styles.compactHeight]: device.docHeight <= 720 ? true : false
        }
    );

    function tabAction(index: number) {
        setFocusedIdx(index);
    }

    function homeAction() {
        dispatch(setPopupMessage({ type: EMessagesTypes.logoutConfirmation }));
    }

    function toogleInGameHistoryPanel() {
        onClose();
        dispatch(setInGameHistoryMode(!inGameHistoryMode));
    }

    function setSettingsBaseSize(value: number) {
        dispatch(setGlobalStyles({ settingsBaseFontSize: value }));
    }

    useEffect(() => {
        if (wcaMode) {
            setTimeout(() => {
                const tabIndex = document.querySelector(`#${DOM_ID.SETTINGS_CONTENT_DATA}`)?.querySelectorAll('[tabindex]');
                if (tabIndex && tabIndex?.length > 0) {
                    (tabIndex[0] as HTMLElement).focus();
                }
            }, 500);
        }
    }, [focusedIdx]);

    useEffect(() => {
        if (isPopupOpen) {
            tabAction(0);
        }
    }, [isPopupOpen]);

    return (
        <div className={containerCn} onPointerDown={handleOverlayClick}>
            <div className={cn(styles.settings_container)}>
                <div className={cn(styles.settings_container_wrapper, globalStyles.wcaMode)}>
                    <div className={styles.settings_content}>
                        <div className={styles.settings_content_header} id={pages[focusedIdx].domId}>
                            <div className={styles.settings_content_title}>{t(pages[focusedIdx].pageHeader)}</div>

                            <div className={styles.settings_content_close}>
                                <button
                                    className={cn(styles.close_button, globalStyles.orientation, globalStyles.wcaMode, globalStyles.screenSize)}
                                    onPointerDown={onClose}
                                ></button>
                            </div>
                        </div>
                        <div
                            id={DOM_ID.SETTINGS_CONTENT_DATA}
                            className={cn(styles.settings_content_data, globalStyles.wcaMode, globalStyles.screenSize, globalStyles.orientation)}
                            aria-description={t(pages[focusedIdx].pageTabDescription)}
                        >
                            {pages[focusedIdx].content}
                        </div>
                    </div>
                </div>
                <div className={styles.settings_container_navigation}>
                    <div
                        className={cn(
                            styles.settings_navigation,
                            globalStyles.orientation,
                            globalStyles.wcaMode,
                            globalStyles.screenSize,
                            state.homeButton ? styles.nav_lenght_5 : styles.nav_lenght_4
                        )}
                    >
                        {pages.map((tab, i) => (
                            <Button
                                key={i}
                                description={t(tab.pageTabDescription)}
                                className={cn(styles.settings_navigation_tab, focusedIdx === tab.id ? styles.active : null)}
                                onChange={() => tabAction(i)}
                            >
                                {tab.pageIcon}
                            </Button>
                        ))}

                        {state.inGameHistoryButton && (
                            <Button
                                id={DOM_ID.SETTINGS_PAGES_TAB_INGAME_HISTORY}
                                description={t('SETTINGS_PAGE_TAB_WCA_INGAME_HISTORY')}
                                className={styles.settings_navigation_tab}
                                onChange={() => toogleInGameHistoryPanel()}
                            >
                                {InGameHistoryIcon}
                            </Button>
                        )}

                        {state.homeButton && (
                            <Button
                                id={DOM_ID.SETTINGS_PAGES_TAB_HOME}
                                description={t('SETTINGS_PAGE_TAB_WCA_LOBBY')}
                                className={styles.settings_navigation_tab}
                                onChange={() => homeAction()}
                            >
                                {HomeIcon}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MenuPagesPopup;

/*
        <TextField ofHeightValue={1.5} onResizeEnd={(value: number) => setSettingsBaseSize(value)}>
                                    {t(pages[focusedIdx].pageHeader)}
                                </TextField>
*/
